import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { Text } from "@src/lib/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import {
  getExistingSessionEmail,
  getExistingSessionUsername,
} from "@src/appV2/Auth/cognito/cognitoAuth";
import { startSsoRedirect } from "@src/appV2/Auth/RipplingSso/startSsoRedirect";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useResendCooldown } from "@src/appV2/redesign/Login/useResendCooldown";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { useEffect, useRef, useState } from "react";

import {
  CognitoSideChannelError,
  requestEmailOtpSideChannel,
  revokeTokenSideChannel,
  signInWithPasswordSideChannel,
  verifyEmailOtpSideChannel,
} from "@src/appV2/Auth/cognito/cognitoSideChannel";
import { useElevateStepUp } from "./api/useElevateStepUp";
import { STEP_UP_EVENTS } from "./constants";
import { type StepUpMethod } from "./stepUpController";

interface StepUpModalProps {
  open: boolean;
  stepUpMethod: StepUpMethod;
  onComplete: () => void;
  onCancel: () => void;
}

type WorkplaceFactor = "otp" | "password";
type OtpPhase = "idle" | "sending" | "codeEntry" | "verifying";

// The factor-order knob: which factor workplace users see first (both stay reachable
// via the toggle link, so flipping this is the only reorder change).
const INITIAL_WORKPLACE_FACTOR: WorkplaceFactor = "otp";
// Cognito EMAIL_OTP codes are 8 digits for this pool. Verify unlocks at 6 so an
// environment issuing shorter codes never dead-ends the button; Cognito is the
// authority on whether the code is right.
const OTP_CODE_MAX_LENGTH = 8;
const OTP_CODE_MIN_LENGTH = 6;
// Deliberately shorter than the login flow's 60s default: a step-up interrupts active work.
const OTP_RESEND_COOLDOWN_SECONDS = 30;

function revokeDiscardedSideChannelToken(params: {
  refreshToken?: string;
  factor: WorkplaceFactor;
}): void {
  if (!isDefined(params.refreshToken)) {
    return;
  }

  void revokeTokenSideChannel({ refreshToken: params.refreshToken }).catch((error: unknown) => {
    logError(APP_V2_APP_EVENTS.COGNITO_AUTH_UNEXPECTED_ERROR, {
      error,
      metadata: {
        operation: "revoke-step-up-side-channel-token",
        factor: params.factor,
      },
    });
  });
}

/**
 * PHI step-up prompt (HIPAA §164.312(a)(1)).
 *
 * Workplace users (`otp` method; `magic_link` tolerated for deploy-order): email-OTP
 * first — "Send code" runs the EMAIL_OTP challenge over a Cognito side channel (the
 * primary session is never signed out, so cancel/wrong-code are harmless), the user
 * types the emailed code, and the fresh ID token elevates on home-health-api. "Use your
 * password instead" swaps to a password sign-in over the same side channel. Either way
 * the original request retries on the UNCHANGED session.
 *
 * Employees (`sso` method): full redirect back through the Rippling Hosted-UI login
 * (`prompt=LOGIN`). On return the session itself carries a fresh `auth_time`, so PHI
 * queries pass the interceptor without any elevate call.
 */
export function StepUpModal(props: StepUpModalProps): JSX.Element {
  const { open, stepUpMethod, onComplete, onCancel } = props;
  const [factor, setFactor] = useState<WorkplaceFactor>(INITIAL_WORKPLACE_FACTOR);
  const [otpPhase, setOtpPhase] = useState<OtpPhase>("idle");
  const [otpCode, setOtpCode] = useState("");
  const [password, setPassword] = useState("");
  const [isWorking, setIsWorking] = useState(false);
  const [accountEmail, setAccountEmail] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  // The verify call must replay the exact USERNAME the send used, on the session the
  // send minted — one ref holding both, so the pair can't drift apart (and a ref, not
  // state, because no render depends on it).
  const challengeRef = useRef<{ username: string; session: string } | undefined>(undefined);
  const { secondsRemaining, isCoolingDown, startCooldown } = useResendCooldown(
    OTP_RESEND_COOLDOWN_SECONDS
  );
  const elevateStepUp = useElevateStepUp();

  useEffect(() => {
    void getExistingSessionEmail().then(setAccountEmail);
  }, []);

  // Never auto-send on open: every send burns a Cognito rate-limit slot, and a user who
  // wants the password factor shouldn't trigger an email at all.
  async function handleSendCode(): Promise<void> {
    setOtpPhase("sending");
    setErrorMessage(undefined);
    try {
      // Resends reuse the username the first send resolved; only the first send (or a
      // send after an expired challenge) hits the session.
      const username =
        challengeRef.current?.username ??
        (await getExistingSessionUsername()) ??
        (await getExistingSessionEmail());
      if (!username) {
        setErrorMessage("Couldn't determine your account. Please sign in again.");
        setOtpPhase("idle");
        return;
      }

      const { session } = await requestEmailOtpSideChannel(username);
      challengeRef.current = { username, session };
      setOtpCode("");
      setOtpPhase("codeEntry");
      startCooldown();
    } catch (error) {
      if (error instanceof CognitoSideChannelError && error.isRateLimited) {
        setErrorMessage("Too many attempts — please wait a few minutes and try again.");
      } else if (error instanceof CognitoSideChannelError && error.type === "UnexpectedChallenge") {
        setErrorMessage("Your account can't receive email codes here — use your password instead.");
      } else {
        setErrorMessage("We couldn't send a code. Please try again.");
      }
      setOtpPhase("idle");
    }
  }

  async function handleVerifyCode(): Promise<void> {
    const challenge = challengeRef.current;
    if (!challenge) {
      setErrorMessage("That code expired. Send a new one.");
      setOtpPhase("idle");
      return;
    }

    setOtpPhase("verifying");
    setErrorMessage(undefined);
    try {
      const { freshIdToken, refreshToken } = await verifyEmailOtpSideChannel({
        ...challenge,
        code: otpCode,
      });
      try {
        await elevateStepUp.mutateAsync(freshIdToken);
      } finally {
        revokeDiscardedSideChannelToken({ refreshToken, factor: "otp" });
      }
      onComplete();
    } catch (error) {
      logEvent(STEP_UP_EVENTS.FAILED, { stepUpMethod, factor: "otp" });
      if (error instanceof CognitoSideChannelError && error.isWrongOtp) {
        // Cognito allows a few attempts on the same challenge session — keep it.
        setErrorMessage("That code didn't work. Please try again.");
        setOtpCode("");
        setOtpPhase("codeEntry");
      } else if (error instanceof CognitoSideChannelError && error.isExpiredSession) {
        challengeRef.current = undefined;
        setOtpCode("");
        setErrorMessage("That code expired. Send a new one.");
        setOtpPhase("idle");
      } else if (error instanceof CognitoSideChannelError && error.isRateLimited) {
        setErrorMessage("Too many attempts — please wait a few minutes and try again.");
        setOtpPhase("codeEntry");
      } else {
        setErrorMessage("Something went wrong. Please try again.");
        setOtpPhase("codeEntry");
      }
    }
  }

  async function handlePasswordVerify(): Promise<void> {
    setIsWorking(true);
    setErrorMessage(undefined);
    try {
      const email = await getExistingSessionEmail();
      if (!email) {
        setErrorMessage("Couldn't determine your account email. Please sign in again.");
        return;
      }

      const { freshIdToken, refreshToken } = await signInWithPasswordSideChannel({
        email,
        password,
      });
      try {
        await elevateStepUp.mutateAsync(freshIdToken);
      } finally {
        revokeDiscardedSideChannelToken({ refreshToken, factor: "password" });
      }
      onComplete();
    } catch (error) {
      logEvent(STEP_UP_EVENTS.FAILED, { stepUpMethod, factor: "password" });
      if (error instanceof CognitoSideChannelError && error.type === "NotAuthorizedException") {
        setErrorMessage("That password didn't work. Try again, or use the emailed code.");
      } else if (error instanceof CognitoSideChannelError && error.type === "ChallengeRequired") {
        setErrorMessage(
          "Your account needs an extra sign-in step we can't run here — use the emailed code instead."
        );
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
    } finally {
      setIsWorking(false);
    }
  }

  async function handleSsoRedirect(): Promise<void> {
    setIsWorking(true);
    setErrorMessage(undefined);
    try {
      // Full-page redirect through the Rippling Hosted UI with prompt=LOGIN; the page
      // unloads here and returns to this path with a fresh auth_time on the session.
      await startSsoRedirect(`${globalThis.location.pathname}${globalThis.location.search}`, {
        source: "step-up",
      });
    } catch {
      setErrorMessage("Couldn't start the SSO sign-in. Please try again.");
      setIsWorking(false);
    }
  }

  function handleToggleFactor(): void {
    setErrorMessage(undefined);
    setFactor(factor === "otp" ? "password" : "otp");
  }

  // While a send/verify (or SSO start) is in flight, backdrop/Escape/Cancel must not
  // dismiss the dialog — the elevate could land after the user backed out. Code entry
  // at rest stays dismissible. Cancel = onCancel() = soft block, session untouched.
  const isBusy = otpPhase === "sending" || otpPhase === "verifying" || isWorking;

  function handleDialogClose(): void {
    if (isBusy) {
      return;
    }
    onCancel();
  }

  const isSso = stepUpMethod === "sso";
  const isOtpFactor = !isSso && factor === "otp";
  const isPasswordFactor = !isSso && factor === "password";
  const isAwaitingCode = otpPhase === "codeEntry" || otpPhase === "verifying";
  // Single source of truth for the Verify button AND the Enter-key shortcut.
  const canVerifyOtp = otpPhase !== "verifying" && otpCode.length >= OTP_CODE_MIN_LENGTH;
  const emailCopy = accountEmail ?? "your email";

  let bodyCopy: string;
  if (isSso) {
    bodyCopy =
      "Your access to patient data has timed out. Sign in again with SSO to keep viewing — you'll come right back to this page.";
  } else if (isOtpFactor) {
    bodyCopy = isAwaitingCode
      ? `We emailed a verification code to ${emailCopy}. Enter it below to keep viewing.`
      : `Your access to patient data has timed out. We'll email a verification code to ${emailCopy} to keep viewing. You'll stay signed in either way.`;
  } else {
    bodyCopy =
      "Your access to patient data has timed out. Enter your password to keep viewing. You'll stay signed in either way.";
  }

  let primaryAction: { label: string; onClick: () => Promise<void>; disabled: boolean };
  if (isSso) {
    primaryAction = { label: "Sign in with SSO", onClick: handleSsoRedirect, disabled: isWorking };
  } else if (isOtpFactor && !isAwaitingCode) {
    primaryAction = {
      label: "Send code",
      onClick: handleSendCode,
      disabled: otpPhase === "sending",
    };
  } else if (isOtpFactor) {
    primaryAction = {
      label: "Verify",
      onClick: handleVerifyCode,
      disabled: !canVerifyOtp,
    };
  } else {
    primaryAction = {
      label: "Verify",
      onClick: handlePasswordVerify,
      disabled: isWorking || password.length === 0,
    };
  }

  return (
    <BerlinThemeProvider>
      <Dialog open={open} onClose={handleDialogClose}>
        <DialogTitle>Re-authenticate to view PHI</DialogTitle>
        <DialogContent>
          {/* The OTP copy embeds the account email; keep it out of RUM replays. */}
          <DialogContentText data-dd-privacy="mask">{bodyCopy}</DialogContentText>
          {isOtpFactor && isAwaitingCode ? (
            <TextField
              // Focusing the field inside a just-opened dialog is the accepted a11y
              // exception to no-autofocus (it moves focus INTO the dialog).
              // oxlint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              fullWidth
              label="Verification code"
              value={otpCode}
              onChange={(event) =>
                setOtpCode(event.target.value.replace(/\D/g, "").slice(0, OTP_CODE_MAX_LENGTH))
              }
              onKeyDown={(event) => {
                if (event.key === "Enter" && canVerifyOtp) {
                  void handleVerifyCode();
                }
              }}
              autoComplete="one-time-code"
              inputProps={{ inputMode: "numeric" }}
              disabled={otpPhase === "verifying"}
              sx={{ mt: 2 }}
            />
          ) : null}
          {isPasswordFactor ? (
            <TextField
              // oxlint-disable-next-line jsx-a11y/no-autofocus -- same a11y exception
              autoFocus
              fullWidth
              type="password"
              label="Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && password.length > 0 && !isWorking) {
                  void handlePasswordVerify();
                }
              }}
              autoComplete="current-password"
              disabled={isWorking}
              sx={{ mt: 2 }}
            />
          ) : null}
          {errorMessage ? (
            <Text color="error" variant="body2" sx={{ mt: 2 }}>
              {errorMessage}
            </Text>
          ) : null}
          {isOtpFactor && isAwaitingCode ? (
            <Button
              variant="text"
              onClick={handleSendCode}
              disabled={isCoolingDown || otpPhase === "verifying"}
              sx={{ mt: 1 }}
            >
              {isCoolingDown ? `Resend in ${secondsRemaining}s` : "Resend code"}
            </Button>
          ) : null}
          {isSso ? null : (
            <Button variant="text" onClick={handleToggleFactor} disabled={isBusy} sx={{ mt: 1 }}>
              {isOtpFactor ? "Use your password instead" : "Email me a code instead"}
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onCancel} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            startIcon={
              isBusy && !isSso ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {primaryAction.label}
          </Button>
        </DialogActions>
      </Dialog>
    </BerlinThemeProvider>
  );
}
